'use client';

import {
  createContext,
  use,
  useCallback,
  useMemo,
  useState,
  ReactNode
} from 'react';
import { usePathname } from 'next/navigation';

interface NavigationLoadingState {
  isNavigating: boolean;
  destination: string;
}

interface NavigationLoadingContextValue {
  navigationState: NavigationLoadingState;
  startNavigation: (destination: string) => void;
  endNavigation: () => void;
}

const NavigationLoadingContext = createContext<
  NavigationLoadingContextValue | undefined
>(undefined);

export function NavigationLoadingProvider({
  children
}: {
  children: ReactNode;
}) {
  const pathname = usePathname();
  const [navigationState, setNavigationState] =
    useState<NavigationLoadingState>({
      isNavigating: false,
      destination: ''
    });

  // Clear navigation state when the pathname changes (navigation completed)
  // using the "store info from previous renders" pattern.
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    if (navigationState.isNavigating) {
      setNavigationState({ isNavigating: false, destination: '' });
    }
  }

  const startNavigation = useCallback((destination: string) => {
    setNavigationState({ isNavigating: true, destination });
  }, []);

  const endNavigation = useCallback(() => {
    setNavigationState({ isNavigating: false, destination: '' });
  }, []);

  const value = useMemo<NavigationLoadingContextValue>(
    () => ({ navigationState, startNavigation, endNavigation }),
    [navigationState, startNavigation, endNavigation]
  );

  return (
    <NavigationLoadingContext value={value}>
      {children}
    </NavigationLoadingContext>
  );
}

export function useNavigationLoading() {
  const context = use(NavigationLoadingContext);
  if (!context) {
    throw new Error(
      'useNavigationLoading must be used within NavigationLoadingProvider'
    );
  }
  return context;
}
